<template>
  <div class="tournament-sponsors-wrapper d-flex flex-column">
    <div
      v-for="key in Object.keys(sponsorLogos).sort(sortDefaultCountry)"
      :key="`sponsor_country_${key}`"
      class="country-logos d-flex flex-column"
    >
      <div class="d-flex mt-5 align-items-center">
        <Subheader
          v-if="key !== defaultCountry"
          :text="countryText(key)"
          class="country-header p-4"
        />
        <div
          v-for="(item, index) in sponsorLogos[key]"
          :key="`sponsor_${key}_${index}`"
          class="logo-item"
        >
          <ImageUpload
            :value="item"
            :width="168"
            :height="132"
            preview-type="contain"
            :name="`sponsor-${key}-${index}`"
            :disabled="isTournamentFieldDisabled('sponsors')"
            :help="
              recommendedSizeDim(168, 132) +
                ', ' +
                $t(
                  '_web_tournament-sponsors_image_label-transparent',
                  'transparent'
                )
            "
            :placeholder="addOneMoreSponsorButtonText(key, item)"
            @input="onImgUploaded($event, key, index)"
          />
        </div>
        <Btn
          v-if="key !== defaultCountry"
          class="h-ml-4 mt-2 remove-country"
          icon="trash"
          :icon-size="24"
          @click.native="removeCountry(key)"
        />
      </div>
      <div
        v-if="key === defaultCountry"
        class="col-12 mt-4 d-flex align-items-center add-country-row row"
      >
        <AutoSuggest
          v-model="country"
          :label="$t('_web_country-sponsors-label', 'Country for sponsors')"
          :source="suggestCountriesRequest"
          name="country"
          :placeholder="
            $t(
              '_web_tournament_country-tournament-placeholder',
              'Enter country'
            )
          "
          display-property="text"
        />

        <Btn
          class="h-ml-4 add-country"
          :disabled="!canAddCountry"
          icon="add"
          :label="$t('_web_country-sponsors-add-country-label', 'Add country')"
          @click.native="addCountry"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from '@/utils/lodashUtils';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import ImageUpload from '@/components/form/ImageUpload';
import Btn from '@/components/atoms/HeraFormBtn.vue';
import AutoSuggest from '@/components/molecules/form/autoComplete/HeraAutosuggest';
import Subheader from '@/components/atoms/Subheader';
import { DEFAULT } from '@/constants/common';

const MAX_NUMBER_OF_SPONSORS_LOGO = 3;

const component = 'prize';
export default {
  name: 'TournamentSponsorsSetting',
  inject: ['$validator'],
  components: {
    ImageUpload,
    AutoSuggest,
    Btn,
    Subheader,
  },
  data() {
    return { country: null, fetchedCountries: [] };
  },
  computed: {
    ...mapGetters([
      'currentTournament',
      'currentTournamentSponsorLogos',
      'isTournamentFieldDisabled',
    ]),
    defaultCountry() {
      return DEFAULT;
    },
    componentData() {
      return this.currentTournament.prize;
    },
    sponsorLogos() {
      const logos = cloneDeep(this.currentTournamentSponsorLogos);
      if (!logos[DEFAULT]) {
        logos[DEFAULT] = [];
      }

      Object.keys(logos).forEach(country => {
        let l = logos[country];
        if (l.length < MAX_NUMBER_OF_SPONSORS_LOGO) {
          logos[country] = [...l, null];
        }
        this.fetchCountry(country);
      });

      return logos;
    },
    sponsorsCount() {
      return this.$lodash.get(this, 'sponsorLogos.length', 0);
    },
    canAddCountry() {
      return this.country && !this.sponsorLogos[this.country.id];
    },
  },
  methods: {
    ...mapActions(['suggestCountriesRequest', 'getCountryByCodeRequest']),
    ...mapMutations(['updateCurrentTournament']),
    addOneMoreSponsorButtonText(key, item) {
      return this.sponsorsCount[key] === 1 || item !== null
        ? this.$t('_web_tournament_add-more-empty-label', 'Upload')
        : this.$t('_web_tournament_add-more-label', 'Add one more');
    },
    onImgUploaded(fileInfo, key, index) {
      const sponsors = this.componentData.sponsors
        ? cloneDeep(this.componentData.sponsors)
        : cloneDeep(this.currentTournamentSponsorLogos);
      if (!sponsors[DEFAULT]) {
        sponsors[DEFAULT] = [];
      }

      sponsors[key][index] = fileInfo;

      this.updateCurrentTournament({
        sponsors,
        component,
      });
      if (!fileInfo) {
        //remove position from array
        let backupSponsors = cloneDeep(this.componentData.sponsors);
        backupSponsors[key].splice(index, 1);
        this.updateCurrentTournament({
          sponsors: backupSponsors,
          component,
        });
      }
    },
    recommendedSizeDim(width, height) {
      return this.$t(
        '_web_tournament_image-recommended-size-dimensions',
        '{width}×{height}px',
        { width, height }
      );
    },
    addCountry() {
      const sponsors = this.componentData.sponsors
        ? cloneDeep(this.componentData.sponsors)
        : cloneDeep(this.currentTournamentSponsorLogos);

      sponsors[this.country.id] = [];
      this.updateCurrentTournament({
        sponsors: sponsors,
        component,
      });

      this.country = null;
    },
    removeCountry(code) {
      const sponsors = this.componentData.sponsors
        ? cloneDeep(this.componentData.sponsors)
        : cloneDeep(this.currentTournamentSponsorLogos);
      delete sponsors[code];
      this.updateCurrentTournament({
        sponsors: sponsors,
        component,
      });
    },
    async fetchCountry(code) {
      if (code !== DEFAULT && !this.fetchedCountries.find(c => c.id == code)) {
        let result = await this.getCountryByCodeRequest(code);
        this.fetchedCountries.push(result);
      }
    },
    countryText(code) {
      let country = this.fetchedCountries.find(c => c.id == code);
      return country ? country.text : code;
    },
    sortDefaultCountry(a, b) {
      return a == DEFAULT ? -1 : b == DEFAULT ? 1 : 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.tournament-sponsors-wrapper {
  .wrapper-control {
    max-width: 180px;
    width: 180px;
  }
  .autosuggest {
    max-width: 240px;
  }
  .add-country {
    min-width: max-content;
    vertical-align: middle;
  }
  .country-header {
    width: 200px;
  }
  .logo-item {
    width: 200px;
  }
}
</style>
